import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  CustomForm,
  CustomFormItem,
  CustomNumberInput,
  CustomSpinSelect,
  OptionSpin,
  Text,
  CustomCheckbox,
  useText,
} from '@src/components';
import { Form, Row, Col, Button, Typography, Image, notification } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  getVehicleLimitTypesDayRestrictionOperations,
  getVehicleLimitValueTypes,
} from '@src/store/slice/addNewCarStore/addCarPlateDetailSlice';
import {
  getVehicleDetailList,
  getUpdateVehicleLimits,
  incrementActiveTab,
} from '@src/store/slice/vehicleManagementStore/vehicleManagementDetailSlice';
import { useMediaQuery } from '@src/hooks/useMediaQuery';
import PO from '@src/assets/icons/ticketcar/4.svg';
import TOTAL from '@src/assets/icons/ticketcar/41.svg';
import BP from '@src/assets/icons/ticketcar/42.svg';
import '@src/styles/addNewCar/addCarSetLimitType.scss';
import { useLanguageChange } from '@src/hooks/useLanguageChange';
import { useDaysOfWeek } from '@src/hooks/useDaysOfWeek';

const { Title } = Typography;

const generateFuelDaysString = (selectedDays, daysOfWeek) => {
  return daysOfWeek.map((day) => (selectedDays.includes(day) ? '1' : '0')).join('');
};

const getIconComponent = (providerType) => {
  switch (providerType) {
    case 4:
      return PO;
    case 41:
      return TOTAL;
    case 42:
      return BP;
    default:
      return null;
  }
};

const VehicleLimitDayRestrictionOperations = ({
  vehicleListDetailLimits,
  vehicleManagementItem,
  vehicleDetailId,
  vehicleId,
  plate,
  vehicleDeviceTypes,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [api, contextHolder] = notification.useNotification();
  const isMobil = useMediaQuery({ query: '(max-width: 1199px)' });
  const { vehicleLimitTypesDayRestrictionOperations, vehicleLimitValueTypes } = useSelector(
    (state) => state.addCarPlateDetail,
  );
  const { loading: loadingLimited } = useSelector((state) => state.vehicleManagementDetail);
  const [checkAll, setCheckAll] = useState(false);
  const [selectedDays, setSelectedDays] = useState([]);
  const [isEditing, setIsEditing] = useState({});
  const [customDetailList, setCustomDetailList] = useState([]);

  const { selectedCustomer } = useSelector((state) => state?.customer);
  const { isAdmin } = useSelector((state) => state.auth);
  const { selectedUserId } = useSelector((state) => state.user);

  const transactionSuccessfullyCompleted2 = useText('transactionSuccessfullyCompleted2');
  const limitDayRestrictionOperations = useText('limitDayRestrictionOperations');
  const limitTypeText = useText('limitType');
  const limitValueTypeText = useText('limitValueType');
  const limitValueText = useText('limitValue');
  const smartRingText = useText('smartRing');
  // The selected days are kept as the (localized) labels themselves, per row, so they have to be
  // moved over to the new labels when the language is switched - otherwise the day buttons look
  // unselected and saving rebuilds the usable-days mask without them.
  const daysOfWeek = useDaysOfWeek((remapDays) => {
    setSelectedDays((current) => {
      if (!current) return current;
      const remapped = Array.isArray(current) ? [] : {};
      Object.keys(current).forEach((rowIndex) => {
        remapped[rowIndex] = remapDays(current[rowIndex]);
      });
      return remapped;
    });
  });

  const fetchVehicleLimitData = async () => {
    await Promise.all(
      vehicleListDetailLimits?.map(async (field) => {
        await dispatch(getVehicleLimitTypesDayRestrictionOperations({ vouId: field.providerType }));
      }),
    );
    await dispatch(
      getVehicleLimitValueTypes({
        custId: isAdmin && selectedUserId ? selectedUserId : selectedCustomer.customerId,
      }),
    );
  };

  const intialVehicleListDetailLimits = async () => {
    const initialSelectedDays = {};
    const initialCheckAll = {};
    const initialIsEditing = {};
    const initialLimitValues = {};
    const initialLimitType = {};
    const initialLimitValueType = {};

    vehicleListDetailLimits?.forEach((field, index) => {
      const daysSelected = daysOfWeek.filter((_, idx) => field.usableDays[idx] === '1');
      initialSelectedDays[index] = daysSelected;
      initialCheckAll[index] = daysSelected.length === daysOfWeek.length;
      initialIsEditing[index] = false;
      initialLimitValues[`limitValue-${index}`] = field.limitAmount;
      initialLimitType[`limitTypeId-${index}`] = vehicleLimitTypesDayRestrictionOperations?.find(
        (limitType) => limitType.limitTypeId === field.limitType,
      )?.limitTypeId;
      initialLimitValueType[`limitValueTypeId-${index}`] = vehicleLimitValueTypes?.find(
        (limitValueType) => limitValueType.id === field.limitValueType,
      )?.id;
    });
    setSelectedDays(initialSelectedDays);
    setCheckAll(initialCheckAll);
    setIsEditing(initialIsEditing);
    form.setFieldsValue({
      ...initialLimitValues,
      ...initialLimitType,
      ...initialLimitValueType,
    });
  };

  // The limit type / limit value type labels are backend text and are only fetched while the lists
  // are empty, so they have to be requested again when the language is switched.
  useLanguageChange(() => {
    fetchVehicleLimitData();
  });

  useEffect(() => {
    if (
      [vehicleLimitTypesDayRestrictionOperations, vehicleLimitValueTypes].every(
        (data) => data.length === 0,
      )
    ) {
      fetchVehicleLimitData();
    }
  }, [vehicleLimitTypesDayRestrictionOperations.length, vehicleLimitValueTypes.length, dispatch]);

  useEffect(() => {
    if (
      vehicleListDetailLimits &&
      vehicleListDetailLimits.length > 0 &&
      [vehicleLimitTypesDayRestrictionOperations, vehicleLimitValueTypes].every(
        (data) => data.length !== 0,
      )
    ) {
      intialVehicleListDetailLimits();
    }
  }, [
    vehicleListDetailLimits,
    vehicleLimitTypesDayRestrictionOperations.length,
    vehicleLimitValueTypes.length,
    form,
  ]);

  useEffect(() => {
    if (vehicleListDetailLimits?.length > 0) {
      try {
        let cArr = [];
        vehicleListDetailLimits.map((item) => {
          if (item.device === 'UTTS' || item.device?.includes('UTTS')) {
            let findIndex = -1;
            findIndex = vehicleListDetailLimits.findIndex(
              (cItem) => cItem.device === smartRingText || cItem.device?.includes(smartRingText),
            );
            if (findIndex !== -1) {
              cArr.push(findIndex);
            }
          } else if (item.device === smartRingText || item.device?.includes(smartRingText)) {
            let findIndex = -1;
            findIndex = vehicleListDetailLimits.findIndex(
              (cItem) => cItem.device === 'UTTS' || cItem.device?.includes('UTTS'),
            );
            if (findIndex !== -1) {
              cArr.push(cArr.length);
            }
          }
        });
        setCustomDetailList(cArr);
      } catch (error) {
        console.error('error', error);
      }
    }
  }, [vehicleListDetailLimits]);

  const intialHandleSave = async (index) => {
    await intialVehicleListDetailLimits();
    await setIsEditing((prev) => ({ ...prev, [index]: false }));

    setTimeout(async () => {
      await dispatch(
        getVehicleDetailList({
          customerId: isAdmin && selectedUserId ? selectedUserId : selectedCustomer.customerId,
          id: vehicleId,
          providerType: vehicleManagementItem.providerType,
        }),
      );
      await dispatch(incrementActiveTab('1'));
    }, 1500);
  };

  const handleCheckAllChange = (index, e) => {
    const checked = e.target.checked;
    const allDays = daysOfWeek;
    const updatedSelectedDays = checked ? allDays : [];

    setCheckAll((prev) => ({ ...prev, [index]: checked }));
    setSelectedDays((prev) => ({ ...prev, [index]: updatedSelectedDays }));
  };

  const handleDayChange = (index, day) => {
    const updatedDays = selectedDays[index].includes(day)
      ? selectedDays[index].filter((d) => d !== day)
      : [...selectedDays[index], day];

    setSelectedDays((prev) => ({ ...prev, [index]: updatedDays }));
    setCheckAll((prev) => ({ ...prev, [index]: updatedDays.length === 7 }));
  };

  const validateLimitValue = (_, value, index) => {
    const limitTypeId = form.getFieldValue(`limitTypeId-${index}`);
    const limitValueTypeId = form.getFieldValue(`limitValueTypeId-${index}`);

    if (value && (!limitTypeId || !limitValueTypeId)) {
      return Promise.reject(new Error('Lütfen önce Limit Tipi ve Limit Değer Tipi seçin.'));
    }

    return Promise.resolve();
  };

  const handleEditClick = (index) => {
    setIsEditing((prev) => ({ ...prev, [index]: true }));
  };

  const handleSave = (index) => {
    const fieldNames = [`limitValue-${index}`, `limitTypeId-${index}`, `limitValueTypeId-${index}`];
    const selectedUsableDays = selectedDays[index];
    const fuelDaysString = generateFuelDaysString(selectedUsableDays, daysOfWeek);

    form
      .validateFields(fieldNames)
      .then(async (values) => {
        // Yalnızca bu index'e ait form değerleri alınacak
        const filteredValues = {
          limitValue: values[`limitValue-${index}`],
          limitTypeId: values[`limitTypeId-${index}`],
          limitValueTypeId: values[`limitValueTypeId-${index}`],
        };

        const limitedValue = {
          vehicleDetailId: Number(vehicleDeviceTypes[index].vehicleDetailId),
          limitType: filteredValues.limitTypeId,
          limitValueType: filteredValues.limitValueTypeId,
          limitAmount: filteredValues.limitValue,
          usableDays: fuelDaysString,
          plate,
        };
        const response = await dispatch(getUpdateVehicleLimits({ limitedValue })).unwrap();
        if (response.isSuccess) {
          api.success({
            message: limitDayRestrictionOperations,
            description: transactionSuccessfullyCompleted2,
            duration: 1,
          });
          await intialHandleSave(index);
        }
      })
      .catch((info) => {
        console.log('Validate Failed for index:', index, info);
      });
  };

  const handleCancel = (index) => {
    setIsEditing((prev) => ({ ...prev, [index]: false }));
    const daysSelected = daysOfWeek.filter(
      (_, idx) => vehicleListDetailLimits[index].usableDays[idx] === '1',
    );
    setSelectedDays((prev) => ({ ...prev, [index]: daysSelected }));
    setCheckAll((prev) => ({ ...prev, [index]: daysSelected.length === daysOfWeek.length }));
    form.setFieldsValue({
      [`limitValue-${index}`]: vehicleListDetailLimits[index].limitAmount,
      [`limitTypeId-${index}`]: vehicleLimitTypesDayRestrictionOperations.find(
        (limitType) => limitType.limitTypeId === vehicleListDetailLimits[index].limitType,
      )?.limitTypeId,
      [`limitValueTypeId-${index}`]: vehicleLimitValueTypes.find(
        (limitValueType) => limitValueType.id === vehicleListDetailLimits[index].limitValueType,
      )?.id,
    });
  };

  return (
    <div style={{ padding: '30px 15px' }}>
      {contextHolder}
      <Row gutter={[16, 16]}>
        <Col span={24}>
          {vehicleListDetailLimits?.map((field, index) => {
            if (customDetailList?.filter((cItem) => cItem === index).length === 0) {
              return (
                <div key={`${field.providerType}-${index}`}>
                  <Row justify="space-between" align="middle" style={{ paddingBottom: '25px' }}>
                    <Col>
                      <Row align="middle">
                        <Image
                          src={getIconComponent(field.providerType)}
                          alt={`provider-${index}`}
                          width={50}
                          preview={false}
                          style={{ marginLeft: isMobil ? '5px' : '10px' }}
                        />
                        <Title
                          level={4}
                          style={{
                            fontSize: isMobil ? 16 : 20,
                            marginLeft: isMobil ? '15px' : '25px',
                            marginTop: '10px',
                          }}
                        >
                          {field.providerImage}
                        </Title>
                        <Title
                          level={4}
                          style={{
                            fontSize: isMobil ? 16 : 20,
                            marginLeft: '5px',
                            marginTop: '10px',
                          }}
                        >
                          {field.device}
                        </Title>
                      </Row>
                    </Col>
                    <Col>
                      {isEditing[index] ? (
                        <div
                          style={{
                            display: 'flex',
                            flex: 'auto',
                            justifyContent: 'end',
                            gap: '10px',
                          }}
                        >
                          <Button
                            style={{ backgroundColor: '#ff4d4f', color: '#fff' }}
                            onClick={() => handleCancel(index)}
                          >
                            <Text t="giveUp" />
                          </Button>
                          <Button
                            style={{ backgroundColor: '#00a483', color: '#fff' }}
                            onClick={() => handleSave(index)}
                            loading={loadingLimited.vehicleUpdateLimits}
                          >
                            <Text t="cardParameterSave" />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          type="text"
                          icon={<EditOutlined style={{ color: '#0f172a', fontSize: '24px' }} />}
                          onClick={() => handleEditClick(index)}
                          style={{ display: 'flex', alignItems: 'end', marginLeft: '8px' }}
                        >
                          <Text t="edit2" />
                        </Button>
                      )}
                    </Col>
                  </Row>
                  <div className="add-car-set-limit-type">
                    <CustomForm
                      name={`limitDetails-edit-${index}`}
                      className="filter-form"
                      autoComplete="off"
                      initialValues={{}}
                      layout={!isMobil ? 'horizontal' : 'vertical'}
                      form={form}
                    // onValuesChange={handleValuesChange}
                    >
                      <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }} className="form-item">
                        <Col span={isMobil ? 24 : 8}>
                          <CustomFormItem
                            label={<Text t="limitType" />}
                            name={`limitTypeId-${index}`}
                            className={'delivery-address-county-form-item'}
                          >
                            <CustomSpinSelect
                              id="add-car-set-limit-type-limitTypeId-select"
                              placeholder={limitTypeText}
                              disabled={
                                !isEditing[index] ||
                                vehicleLimitTypesDayRestrictionOperations.length === 0
                              }
                              loading={vehicleLimitTypesDayRestrictionOperations.length === 0}
                              height={36}
                            >
                              {vehicleLimitTypesDayRestrictionOperations
                                ?.filter((vehicle) => vehicle.vouId === field.providerType)
                                ?.map((vehicle) => (
                                  <OptionSpin
                                    id={`add-car-set-limit-type-limitTypeId-${vehicle?.limitTypeLabel}-option`}
                                    key={vehicle?.limitTypeId}
                                    value={vehicle?.limitTypeId}
                                  >
                                    {vehicle?.limitTypeLabel}
                                  </OptionSpin>
                                ))}
                            </CustomSpinSelect>
                          </CustomFormItem>
                        </Col>

                        <Col span={isMobil ? 24 : 8}>
                          <CustomFormItem
                            label={<Text t="limitValueType" />}
                            name={`limitValueTypeId-${index}`}
                            className={'delivery-address-county-form-item'}
                          >
                            <CustomSpinSelect
                              id="add-car-set-limit-type-limitValueTypeId-select"
                              placeholder={limitValueTypeText}
                              disabled={!isEditing[index] || vehicleLimitValueTypes.length === 0}
                              loading={vehicleLimitValueTypes.length === 0}
                              height={36}
                            >
                              {vehicleLimitValueTypes?.map((vehicle) => (
                                <OptionSpin
                                  id={`add-car-set-limit-type-limitValueTypeId-${vehicle?.limitValueTypeLabel}-option`}
                                  key={vehicle?.id}
                                  value={vehicle?.id}
                                >
                                  {vehicle?.limitValueTypeLabel}
                                </OptionSpin>
                              ))}
                            </CustomSpinSelect>
                          </CustomFormItem>
                        </Col>

                        <Col span={isMobil ? 24 : 8}>
                          <CustomFormItem
                            label={<Text t="limitValue" />}
                            name={`limitValue-${index}`}
                            className={'delivery-address-county-form-item'}
                            rules={[
                              {
                                validator: (rule, value) => validateLimitValue(rule, value, index),
                              },
                            ]}
                          >
                            <CustomNumberInput
                              id="add-limit-value-numberInput"
                              autoComplete="off"
                              placeholder={limitValueText}
                              height={36}
                              disabled={!isEditing[index]}
                            />
                          </CustomFormItem>
                        </Col>
                      </Row>
                    </CustomForm>
                    {field.usableDays ? (
                      <div className="responsive-container">
                        <div className="top-row">
                          <div className="question">
                            <Text t="whichDaysCanThisVehiclePurchaseFuel" />
                          </div>
                          <div
                            className={
                              !isEditing[index]
                                ? 'checkbox-container-disabled'
                                : 'checkbox-container'
                            }
                          >
                            <CustomCheckbox
                              checked={checkAll[index] || false}
                              onChange={(e) => handleCheckAllChange(index, e)}
                            >
                              <span className="address-form-item">
                                <Text t="allDays" />
                              </span>
                            </CustomCheckbox>
                          </div>
                        </div>
                        <div className="input-container">
                          {daysOfWeek.map((day) => (
                            <Button
                              key={day}
                              type={selectedDays[index]?.includes(day) ? 'primary' : 'default'}
                              onClick={() => handleDayChange(index, day)}
                              disabled={!isEditing[index]}
                            >
                              {day}
                            </Button>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            } else return null;
          })}
        </Col>
      </Row>
    </div>
  );
};

VehicleLimitDayRestrictionOperations.propTypes = {
  vehicleListDetailLimits: PropTypes.arrayOf(PropTypes.object),
  vehicleManagementItem: PropTypes.object,
  vehicleDetailId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  vehicleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  plate: PropTypes.string,
  vehicleDeviceTypes: PropTypes.array,
};

export default VehicleLimitDayRestrictionOperations;
